import { Link } from 'react-router-dom';
import { HomeIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

export default function SocietyAccount() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Society and Association Account"
        description="Tailored banking for societies, associations and community groups in Somalia. Joint control, standing orders and transparent records from MyBank."
        keywords={['society account Somalia', 'association banking', 'community group account', 'MyBank society account']}
      />
      {/* Hero Section */}
      <div className="relative min-h-[300px] sm:h-[390px] bg-gradient-to-r from-[#001F5F] to-[#017EFF] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center space-y-6 sm:space-y-12 py-8 sm:py-0">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-white/90 flex-wrap">
              <Link to="/" className="hover:text-[#017EFF] transition-colors flex items-center">
                <HomeIcon className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                Home
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <Link to="/#banking-solutions" className="hover:text-[#017EFF] transition-colors hidden sm:inline">
                Corporate Banking
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2 hidden sm:inline">/</span>
              <Link to="/services" className="hover:text-[#017EFF] transition-colors">
                Current Accounts
              </Link>
              <span className="text-white/60 mx-1 sm:mx-2">/</span>
              <span className="text-[#017EFF]">Society Account</span>
            </nav>

            {/* Hero Content */}
            <div className="flex flex-col items-center max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight text-center mb-4 sm:mb-6 px-2">
                Society <span className="text-[#017EFF]">Account</span>
              </h1>
              <p className="text-sm sm:text-lg md:text-xl text-white/90 text-center leading-relaxed px-4 max-w-3xl">
                Tailored banking solutions for societies and associations
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-4 -mt-8 sm:-mt-16 mb-12 sm:mb-24">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-start">
            {/* Left Column */}
            <div className="space-y-6 sm:space-y-8 lg:space-y-10">
              <div className="space-y-4 sm:space-y-6">
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#001F5F]">
                  Dedicated Banking for Societies
                </h2>
                <div className="space-y-4 text-gray-700">
                  <p className="text-sm sm:text-base leading-relaxed">
                    Societies and associations have unique financial management needs—from membership fee collection to fund disbursements and transparent reporting.
                  </p>
                  <p className="text-sm sm:text-base leading-relaxed">
                    Our Society Account is designed to streamline your society's operations, ensuring efficient transactions and clear audit trails.
                  </p>
                </div>
              </div>

              {/* Benefits List */}
              <div className="bg-gray-50 rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-[#001F5F] mb-4 sm:mb-6 lg:mb-8">
                  Account Benefits
                </h3>
                <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:gap-6">
                  {[
                    "50‑leaf checkbook",
                    "Membership fees collection",
                    "Batch payments and disbursements",
                    "Certified digital statements for audits",
                    "Standing order & direct debit setup",
                    "Internet banking & mobile access",
                    "SMS alerts for all transactions",
                    "Convenient inward/outward transfers",
                    "Dedicated relationship manager"
                  ].map((benefit, idx) => (
                    <div key={idx} className="flex items-start text-gray-700">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-100 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="pt-2 sm:pt-4">
                <Link
                  to="/open-account"
                  className="inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 bg-[#017EFF] text-white font-semibold rounded-lg hover:bg-[#0165CC] transition-all transform hover:scale-105 hover:shadow-lg text-sm sm:text-base w-full sm:w-auto justify-center"
                >
                  Open Account
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6 sm:space-y-8 mt-8 lg:mt-0">
              {/* Requirements */}
              <div className="bg-[#001F5F] rounded-lg sm:rounded-xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-4 sm:mb-6 lg:mb-8">
                  Requirements
                </h3>
                <div className="space-y-4 sm:space-y-6">
                  {[
                    "Society registration certificate",
                    "Constitution & bylaws document",
                    "List of office bearers with IDs",
                    "Board resolution authorizing account opening",
                    "Proof of society's physical address",
                    "Passport‑size photos of signatories",
                    "Valid ID copies of authorized signatories",
                    "Letter of request with signing mandate"
                  ].map((req, idx) => (
                    <div key={idx} className="flex items-start text-white/90">
                      <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-[#017EFF]/20 flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 sm:w-5 sm:h-5 text-[#017EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base">{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Additional Services */}
              {/* <div className="bg-gray-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-2xl font-bold text-[#001F5F] mb-6">
                  Additional Services
                </h3>
                <div className="space-y-4">
                  <Link to="/internet-and-mobile-banking" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Internet Banking
                  </Link>
                  <Link to="/other-services" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Batch Payments
                  </Link>
                  <Link to="/other-services" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Standing Orders
                  </Link>
                  <Link to="/swift" className="flex items-center text-gray-700 hover:text-[#017EFF] transition-colors">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                    Swift Transfers
                  </Link>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
